import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import type { z } from 'zod';
import {
  farmerCardsCollectionSchema,
  safePinCollectionSchema,
  validateLayer,
  type FarmerCardsCollection,
} from './schemas';

const geoServerAxios = axios.create({
  baseURL: import.meta.env.VITE_GEO_SERVER,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  transformResponse: [
    (data) => {
      if (typeof data === 'string') {
        try {
          return JSON.parse(data);
        } catch {
          return data;
        }
      }
      return data;
    },
  ],
});

export const fetchGeoServer = async <T>(
  url: string,
  params: Record<string, unknown> = {},
): Promise<T> => {
  const config: AxiosRequestConfig = { params };
  try {
    const { data } = await geoServerAxios.get<T>(url, config);
    return data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      throw new Error((axiosError.response?.data as string) || axiosError.message);
    }
    throw new Error(error instanceof Error ? error.message : 'An unknown error occurred');
  }
};

// geonode:farmer_cards_public - 5 cohort summary rows driving the headline
// total (SUM(farmer_count) = 13,391) and the cohort cards. Built off
// VITE_GEO_SERVER (same pattern as the campaign layers) so no new baked env var
// is needed. Validated at the boundary before it reaches any component (§8).
export const fetchFarmerCards = async (): Promise<FarmerCardsCollection> => {
  const baseURL = import.meta.env.VITE_GEO_SERVER as string;
  const url =
    '/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=geonode%3Afarmer_cards_public&outputFormat=application%2Fjson';

  const response = await fetch(`${baseURL}${url}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`fetchFarmerCards: HTTP ${response.status}`);
  }
  const text = (await response.text()).replace(/^\uFEFF/, '').trim();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    console.error('fetchFarmerCards: JSON.parse failed', e, text.slice(0, 200));
    throw new Error('fetchFarmerCards: invalid JSON from farmer_cards_public');
  }
  return validateLayer('farmer_cards_public', farmerCardsCollectionSchema, parsed);
};

// D16a - generic fetch for the forest outline polygon layers (tree_project_
// extents + the two Mizan zone layers). Same boundary discipline as the farmer
// layers (§8): parse, validate against a declared schema, throw on any failure
// so the caller renders no outline (fail-visible) rather than a wrong shape.
// A 404/"feature type unknown" (layer not yet published) lands in the same
// path: the error is logged with the layer name and the map simply carries no
// outline - it must never crash the app.
// cqlFilter (2026-09-17): narrows the request server-side so features we do
// not intend to draw never reach the browser. The upstream view is untouched.
export const fetchOutlineLayer = async <T>(
  typeName: string,
  schema: z.ZodType<T>,
  cqlFilter?: string,
): Promise<T> => {
  const baseURL = import.meta.env.VITE_GEO_SERVER as string;
  const filter = cqlFilter ? `&CQL_FILTER=${encodeURIComponent(cqlFilter)}` : '';
  const url = `/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=geonode%3A${typeName}&outputFormat=application%2Fjson${filter}`;
  const response = await fetch(`${baseURL}${url}`, { headers: { Accept: 'application/json' } });
  if (!response.ok) {
    throw new Error(`fetchOutlineLayer(${typeName}): HTTP ${response.status}`);
  }
  const text = (await response.text()).replace(/^\uFEFF/, '').trim();
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    // An unpublished layer answers 200 with an XML ServiceException, which
    // fails JSON.parse - the expected fail-visible path until the founder
    // publishes geonode:tree_project_extents.
    console.warn(
      `[outline] ${typeName}: response is not JSON (layer unpublished or upstream error)`,
      text.slice(0, 200),
    );
    throw new Error(`fetchOutlineLayer(${typeName}): invalid JSON`);
  }
  return validateLayer(typeName, schema, parsed);
};

export const fetchFarmersLayer = async <T>(params: Record<string, unknown> = {}): Promise<T> => {
  const baseURL = import.meta.env.VITE_GEO_SERVER as string;
  // PII fix (2026-08-27): pin layer repointed from geonode:dashboard_combined to
  // geonode:dashboard_combined_public. The old layer served phone/email/income
  // publicly; building the _public path in code closes that exposure regardless
  // of the baked .env value. Old env-driven read deprecated in place (§2):
  //   const url = import.meta.env.VITE_FARMERS_LAYER as string;
  // maxFeatures=20000: the layer holds 4,080 pins across all cohorts. The old
  // cap of 1,000 (inherited from dashboard_combined) truncated the response -
  // the ~421 pins of the three small cohorts fit, but most of Bench Maji's
  // ~3,659 pins fell past the cap and never rendered. 20,000 covers the current
  // layer with headroom; the map clusters, so drawing all points is fine.
  const url =
    '/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=geonode%3Adashboard_combined_public&maxFeatures=20000&outputFormat=application%2Fjson';

  const queryString = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString();

  const fullURL = `${baseURL}${url}${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(fullURL, {
    headers: { Accept: 'application/json, text/plain, */*' },
  });

  if (!response.ok) {
    throw new Error(`fetchFarmersLayer: HTTP ${response.status}`);
  }

  // Read full body as text before parsing - avoids axios stream truncation
  const text = await response.text();
  const cleaned = text.replace(/^\uFEFF/, '').trim();
  // GeoServer appends an XML ServiceException after the JSON on this layer.
  // Strip everything from the first '<' that appears after the JSON closes.
  let jsonStr = cleaned;
  const xmlIndex = cleaned.indexOf('<?xml');
  const serviceExIndex = cleaned.indexOf('<ServiceExceptionReport');
  const cutIndex = xmlIndex !== -1 ? xmlIndex : serviceExIndex !== -1 ? serviceExIndex : -1;
  if (cutIndex !== -1) {
    console.warn('fetchFarmersLayer: Stripping GeoServer XML tail at index', cutIndex);
    jsonStr = cleaned.slice(0, cutIndex).trim();
    // Remove any trailing comma left by an incomplete last feature
    jsonStr = jsonStr.replace(/,\s*$/, '');
    // Ensure the features array and root object are closed
    if (!jsonStr.endsWith('}')) {
      // Close features array and root object if needed
      const lastBrace = jsonStr.lastIndexOf('}');
      jsonStr = jsonStr.slice(0, lastBrace + 1);
      if (!jsonStr.endsWith(']}')) {
        jsonStr = jsonStr + ']}';
      }
    }
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonStr);
  } catch (parseError: unknown) {
    const preview = cleaned.slice(0, 200);
    console.error('fetchFarmersLayer: JSON.parse failed', parseError, preview);
    console.error('fetchFarmersLayer: Response length:', cleaned.length);
    console.error('fetchFarmersLayer: Last 200 chars:', cleaned.slice(-200));
    throw new Error(
      `fetchFarmersLayer: JSON.parse failed at length ${cleaned.length}. Preview: ${preview}…`,
    );
  }
  // Boundary validation (§8), kept separate from the parse catch so a schema
  // failure is reported as such, not mislabeled as a parse error. Unknown keys
  // are stripped by the schema, so any stray PII in the _public layer cannot
  // reach a component.
  return validateLayer('dashboard_combined_public', safePinCollectionSchema, parsed) as T;
};
